import { FilterStateController } from '@/components/dataGrid/entities/types';
import { BaseListResponseWithKeysetPagination } from '@/shared/entities';
import {
  LIST_INITIAL_CURSOR,
  LIST_INITIAL_HAS_NEXT_PAGE,
  LIST_INITIAL_HAS_PREVIOUS_PAGE,
  LIST_INITIAL_PAGE_SIZE,
} from '@/shared/entities/constants/list';
import removeEmptyParams from '@/utils/removeEmptyParams';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { omit } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Parâmetros de cursor enviados ao backend para navegação keyset.
 *
 * - `after`: usado ao avançar, recebe o `endCursor` da página atual.
 * - `before`: usado ao retroceder, recebe o `startCursor` da página atual.
 */
export interface KeysetCursorParams {
  after?: string;
  before?: string;
}

export interface UseKeysetPagedListOptions<Response, Filter> {
  source: (
    filters: Filter, companyId: string
  ) => Promise<BaseListResponseWithKeysetPagination<Response>> | BaseListResponseWithKeysetPagination<Response>;
  paramsToFilter?: FilterStateController;
  companyId?: string;
  externalLoading?: boolean;
  omitParamsToFilter?: string[];
  autoLoad?: boolean;
}

export const useRequestKeysetPaginatedList = <Response, Filter>({
  source,
  paramsToFilter,
  companyId = '',
  omitParamsToFilter = [],
  externalLoading,
  autoLoad,
}: UseKeysetPagedListOptions<Response, Filter>) => {
  const [data, setData] = useState<Response[]>([]);
  const [pageSize, setPageSize] = useState(LIST_INITIAL_PAGE_SIZE);
  const [hasNextPage, setHasNextPage] = useState(LIST_INITIAL_HAS_NEXT_PAGE);
  const [hasPreviousPage, setHasPreviousPage] = useState(LIST_INITIAL_HAS_PREVIOUS_PAGE);
  const [startCursor, setStartCursor] = useState(LIST_INITIAL_CURSOR);
  const [endCursor, setEndCursor] = useState(LIST_INITIAL_CURSOR);
  const [loading, setLoading] = useState(true);

  const sourceRef = useRef(source);
  const canLoadDataRef = useRef(true);
  const omitParamsToFilterRef = useRef(omitParamsToFilter);

  const loadData = useCallback(
    async (companyIdToFilter?: string, filters?: Filter) => {
      if (!canLoadDataRef.current || externalLoading) {
        return;
      }

      await tryOrCatchMessageError(
        async () => {
          const response = await sourceRef.current(
            removeEmptyParams(omit(filters as object, omitParamsToFilterRef.current)),
            companyIdToFilter || '',
          );

          const { pagination } = response

          setData(response.data || response);
          setPageSize(pagination.pageSize || LIST_INITIAL_PAGE_SIZE);
          setHasNextPage(pagination.hasNextPage ?? LIST_INITIAL_HAS_NEXT_PAGE);
          setHasPreviousPage(pagination.hasPreviousPage ?? LIST_INITIAL_HAS_PREVIOUS_PAGE);
          setStartCursor(pagination.startCursor || LIST_INITIAL_CURSOR);
          setEndCursor(pagination.endCursor || LIST_INITIAL_CURSOR);
        },
        {
          tryBefore: () => {
            setLoading(true);
            setData([]);
            canLoadDataRef.current = false;
          },
          tryAfter: () => {
            setLoading(false);
            canLoadDataRef.current = true;
          },
          messageErrorDefault: 'Não foi possível carregar a lista, tente novamente mais tarde.',
        },
      );
    },
    [externalLoading],
  );

  const refreshData = useCallback(() => {
    loadData(companyId, {
      pageSize: LIST_INITIAL_PAGE_SIZE,
      ...paramsToFilter,
    } as Filter);
  }, [companyId, loadData, paramsToFilter]);

  const goToNextPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    loadData(companyId, {
      pageSize,
      ...paramsToFilter,
      lastId: endCursor,
      beforeId: undefined,
    } as Filter);
  }, [companyId, endCursor, hasNextPage, loadData, pageSize, paramsToFilter]);

  const goToPreviousPage = useCallback(() => {
    if (!hasPreviousPage) {
      return;
    }

    loadData(companyId, {
      pageSize,
      ...paramsToFilter,
      beforeId: startCursor,
      lastId: undefined,
    } as Filter);
  }, [companyId, hasPreviousPage, loadData, pageSize, paramsToFilter, startCursor]);

  useEffect(() => {
    if (!externalLoading) {
      sourceRef.current = source;
    }
  }, [externalLoading, source]);

  useEffect(() => {
    setPageSize(Number(paramsToFilter?.pageSize) || LIST_INITIAL_PAGE_SIZE);
  }, [paramsToFilter?.pageSize]);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    loadData(companyId, {
      pageSize: LIST_INITIAL_PAGE_SIZE,
      ...paramsToFilter,
    } as Filter);
  }, [autoLoad, companyId, loadData, paramsToFilter]);

  return {
    data,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    startCursor,
    endCursor,
    loading,
    setPageSize,
    loadData,
    refreshData,
    goToNextPage,
    goToPreviousPage,
  };
};
